import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import {
  FileBarChart,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Download,
  Edit,
  FileText,
  Send
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';
import ImageViewer from '../../components/ImageViewer';
import ImageWithStatus from '../../components/ImageWithStatus';

type NamedRef = { _id: string; name: string };

interface ReportPhoto {
  url: string;
  description?: string;
  timestamp: Date;
}

interface Report {
  _id: string;
  interventionReference: string;
  mission: {
    _id: string;
    missionNumber: string;
    serviceType: { name: string };
    address: string;
    clientId: {
      _id: string;
      firstName: string;
      lastName: string;
      companyName?: string;
    };
    technicianId: { _id: string; firstName: string; lastName: string };
  };
  status: 'draft' | 'pending' | 'completed' | 'sent';
  type: string;
  date: string;
  startTime?: string;
  endTime?: string;
  location: {
    address: string;
    city: string;
    postalCode: string;
  };
  equipment?: string;
  selectedEquipment?: NamedRef[];
  selectedProducts?: NamedRef[];
  selectedKits?: NamedRef[];
  workPerformed: string;
  notes?: string;
  photos: ReportPhoto[]; // tableau d’objets {url,...}
  anomalies?: { description: string; isCritical: boolean }[];
  batutalCompliant: boolean;
  certificateNumber?: string;
  pdfUrl?: string;
  createdBy: string;
}

const ViewReport: React.FC = () => {
  const params = useParams();
  const reportId = (params as any)?.id || (params as any)?.reportId; // supporte :id ou :reportId
  const [report, setReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchReport = async () => {
      if (!reportId) {
        setError('Report ID is missing.');
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        const response = await apiService.reports.getById(reportId);

        // Le backend renvoie { success, data: { report } } (fallbacks inclus)
        const apiReport =
          response?.data?.data?.report ??
          response?.data?.report ??
          response?.data;

        if (!apiReport) {
          throw new Error('Report not found in API response');
        }
        setReport(apiReport);
        console.debug('[ViewReport] Fetched report data:', apiReport);
      } catch (err) {
        console.error('[ViewReport] Error fetching report:', err);
        setError('Failed to load report.');
        toast.error('Erreur lors du chargement du rapport.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [reportId]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Brouillon', icon: FileText },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'En attente', icon: Clock },
      completed: { color: 'bg-green-100 text-green-800', label: 'Terminé', icon: CheckCircle2 },
      sent: { color: 'bg-blue-100 text-blue-800', label: 'Envoyé', icon: Send }
    } as const;

    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return null;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const handleDownloadPdf = async (reportId: string) => {
    const toastId = toast.loading('Génération du PDF en cours...');
    try {
      const response = await apiService.reports.generatePDF(reportId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report-${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url); // Clean up
      toast.success('PDF téléchargé avec succès.', { id: toastId });
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF.', { id: toastId });
      console.error('Error downloading PDF:', error);
    }
  };

  const getComplianceBadge = (isCompliant: boolean) => {
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          isCompliant ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {isCompliant ? (
          <>
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Conforme BATUTA
          </>
        ) : (
          <>
            <AlertTriangle className="w-3 h-3 mr-1" />
            Non conforme
          </>
        )}
      </span>
    );
  };

  const calculateDuration = (startTime?: string, endTime?: string) => {
    if (!startTime || !endTime) return 'N/A';

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startDate = new Date();
    startDate.setHours(startHour, startMinute, 0, 0);

    const endDate = new Date();
    endDate.setHours(endHour, endMinute, 0, 0);

    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1); // interventions de nuit
    }

    const diffMs = endDate.getTime() - startDate.getTime();
    const diffMinutes = Math.round(diffMs / (1000 * 60));

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    return `${hours}h ${minutes}min`;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto p-4 text-center">
          <p>Chargement du rapport...</p>
        </div>
      </Layout>
    );
  }

  if (!report) {
    return (
      <Layout>
        <div className="container mx-auto p-4 text-center">
          <p>Rapport non trouvé.</p>
        </div>
      </Layout>
    );
  }

  /**
   * Construction robuste des URLs d’images :
   * - supporte photos = string | {url:string}
   * - prepend VITE_FILES_BASE_URL si l’URL est relative
   * - filtre les valeurs vides
   */
  const base = import.meta.env.VITE_FILES_BASE_URL ?? '';
  const normalizeUrl = (raw?: string) => {
    if (!raw) return '';
    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    const left = base?.endsWith('/') ? base.slice(0, -1) : base;
    const right = raw.startsWith('/') ? raw : `/${raw}`;
    return `${left}${right}`;
  };

  const allImages = (report?.photos || [])
    .map((p: any) => (typeof p === 'string' ? p : p?.url) as string)
    .map(normalizeUrl)
    .filter((u) => !!u);

  const openViewer = (index: number) => {
    setCurrentImageIndex(index);
    setIsViewerOpen(true);
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + allImages.length) % allImages.length);
  };

  console.debug('[ViewReport] All images to display:', allImages);
  console.log('[ViewReport] report.photos:', report?.photos);

  return (
    <Layout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4 flex items-center">
          <FileBarChart className="w-6 h-6 mr-2" />
          Rapport d&apos;Intervention: {report.interventionReference}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Détails */}
          <Card className="lg:col-span-2 p-6">
            <h2 className="text-xl font-semibold mb-4">Détails du Rapport</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
              <div>
                <p>
                  <span className="font-medium">Mission:</span> {report.mission?.missionNumber}
                </p>
                <p>
                  <span className="font-medium">Type:</span> {report.type}
                </p>
                <p>
                  <span className="font-medium">Date:</span>{' '}
                  {new Date(report.date).toLocaleDateString('fr-FR')}
                </p>
                <p>
                  <span className="font-medium">Heure:</span> {report.startTime} - {report.endTime} (
                  {calculateDuration(report.startTime, report.endTime)})
                </p>
              </div>
              <div>
                <p>
                  <span className="font-medium">Client:</span>{' '}
                  {report.mission?.clientId?.companyName ||
                    `${report.mission?.clientId?.firstName} ${report.mission?.clientId?.lastName}`}
                </p>
                <p>
                  <span className="font-medium">Technicien:</span>{' '}
                  {report.mission?.technicianId?.firstName} {report.mission?.technicianId?.lastName}
                </p>
                <p>
                  <span className="font-medium">Adresse:</span> {report.location?.address},{' '}
                  {report.location?.postalCode} {report.location?.city}
                </p>
                <p>
                  <span className="font-medium">Conformité BATUTA:</span>{' '}
                  {getComplianceBadge(report.batutalCompliant)}
                </p>
                {report.certificateNumber && (
                  <p>
                    <span className="font-medium">Certificat:</span> {report.certificateNumber}
                  </p>
                )}
              </div>
            </div>

            <hr className="my-4" />

            <h3 className="text-lg font-semibold mb-2">Travaux Effectués</h3>
            <p className="text-gray-700 mb-4">{report.workPerformed}</p>

            {report.notes && (
              <>
                <h3 className="text-lg font-semibold mb-2">Notes</h3>
                <p className="text-gray-700 mb-4">{report.notes}</p>
              </>
            )}

            {report.anomalies && report.anomalies.length > 0 && (
              <>
                <h3 className="text-lg font-semibold mb-2">Anomalies</h3>
                <ul className="list-disc list-inside text-gray-700 mb-4">
                  {report.anomalies.map((anomaly, index) => (
                    <li
                      key={index}
                      className={anomaly.isCritical ? 'text-red-600 font-medium' : ''}
                    >
                      {anomaly.description} {anomaly.isCritical && '(Critique)'}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {report.selectedEquipment && report.selectedEquipment.length > 0 && (
              <>
                <h3 className="text-lg font-semibold mb-2">Équipement(s) Utilisé(s)</h3>
                <ul className="list-disc list-inside text-gray-700 mb-4">
                  {report.selectedEquipment.map((eq, index) => (
                    <li key={index}>{eq.name}</li>
                  ))}
                </ul>
              </>
            )}

            {report.selectedProducts && report.selectedProducts.length > 0 && (
              <>
                <h3 className="text-lg font-semibold mb-2">Produit(s) Utilisé(s)</h3>
                <ul className="list-disc list-inside text-gray-700 mb-4">
                  {report.selectedProducts.map((prod, index) => (
                    <li key={index}>{prod.name}</li>
                  ))}
                </ul>
              </>
            )}

            {report.selectedKits && report.selectedKits.length > 0 && (
              <>
                <h3 className="text-lg font-semibold mb-2">Kit(s) Utilisé(s)</h3>
                <ul className="list-disc list-inside text-gray-700 mb-4">
                  {report.selectedKits.map((kit, index) => (
                    <li key={index}>{kit.name}</li>
                  ))}
                </ul>
              </>
            )}

            <div className="flex justify-end space-x-2 mt-4">
              <Button variant="secondary" onClick={() => window.history.back()}>
                Retour
              </Button>
               <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(report._id)}>
                      <Download className="w-4 h-4 mr-1" />
                      PDF
              </Button>
              <Button onClick={() => navigate(`/admin/reports/edit/${report._id}`)}>
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            </div>
          </Card>

          {/* Galerie photos (sans viewer plein écran) */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-2">Galerie photos</h3>

            {allImages.length === 0 ? (
              <p className="text-sm text-gray-500">Aucune image disponible.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {allImages.map((src, i) => (
                  <ImageWithStatus
                    key={i}
                    src={src}
                    alt={`photo-${i}`}
                    className="w-full h-40 cursor-pointer"
                    onClick={() => openViewer(i)}
                  />
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
      {isViewerOpen && (
        <ImageViewer
          images={allImages}
          currentIndex={currentImageIndex}
          onClose={closeViewer}
          onNext={nextImage}
          onPrev={prevImage}
        />
      )}
    </Layout>
  );
};

export default ViewReport;
